import { Lightning } from '@phosphor-icons/react';

const ENERGY_CLASSES = [
    { label: 'A+', maxValue: 25, color: '#00873E' },
    { label: 'A', maxValue: 50, color: '#4FA83D' },
    { label: 'B', maxValue: 75, color: '#9FC62B' },
    { label: 'C', maxValue: 100, color: '#D4DE21' },
    { label: 'D', maxValue: 125, color: '#ECDB12' },
    { label: 'E', maxValue: 150, color: '#EBB114' },
    { label: 'F', maxValue: 175, color: '#E58016' },
    { label: 'G', maxValue: 200, color: '#E04E1B' },
    { label: 'H', maxValue: 250, color: '#C8201F' },
];

function getClassIndex(energyClass) {
    if (!energyClass) return -1;
    const idx = ENERGY_CLASSES.findIndex(c => c.label === energyClass.toUpperCase());
    return idx;
}

function getPositionFromValue(value) {
    if (!value || value <= 0) return null;
    // Map 0-250+ to 0-100%
    const clamped = Math.min(value, 260);
    return (clamped / 260) * 100;
}

export function EnergyEfficiencyScale({ energyClass, energyValue, certificateType, validUntil, condition, yearBuilt }) {
    const classIdx = getClassIndex(energyClass);
    const valuePosition = getPositionFromValue(energyValue);

    const hasData = energyClass || energyValue;
    if (!hasData) return null;

    return (
        <div className="space-y-4" data-testid="energy-scale">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Lightning size={20} weight="fill" className="text-amber-500" />
                    <h3 className="font-semibold text-base">Energieausweis</h3>
                </div>
                {certificateType && (
                    <span className="text-xs bg-muted px-2 py-1 rounded-sm font-medium">
                        {certificateType === 'BEDARF' ? 'Bedarfsausweis' : certificateType === 'VERBRAUCH' ? 'Verbrauchsausweis' : certificateType}
                    </span>
                )}
            </div>

            {/* Energy Value Display */}
            {energyValue && (
                <div className="text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Endenergiebedarf</p>
                    <p className="text-3xl font-bold tracking-tight mt-0.5">
                        {energyValue} <span className="text-base font-normal text-muted-foreground">kWh/(m²·a)</span>
                    </p>
                </div>
            )}

            {/* Color Scale Bar */}
            <div className="relative pt-2 pb-8">
                {/* Arrow Marker - positioned by class or value */}
                {(classIdx >= 0 || valuePosition !== null) && (
                    <div
                        className="absolute -top-1 z-10 flex flex-col items-center transition-all duration-500"
                        style={{
                            left: valuePosition !== null
                                ? `${valuePosition}%`
                                : `${((classIdx + 0.5) / ENERGY_CLASSES.length) * 100}%`,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-foreground" />
                    </div>
                )}

                {/* Scale Bar */}
                <div className="flex h-10 rounded-md overflow-hidden shadow-sm">
                    {ENERGY_CLASSES.map((cls, idx) => (
                        <div
                            key={cls.label}
                            className={`flex-1 flex items-center justify-center relative transition-all ${
                                classIdx === idx ? 'scale-y-110 z-10 shadow-lg ring-2 ring-foreground/30' : ''
                            }`}
                            style={{ backgroundColor: cls.color }}
                        >
                            <span className={`text-white font-bold text-sm drop-shadow-md ${
                                classIdx === idx ? 'text-base' : ''
                            }`}>
                                {cls.label}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Value Labels */}
                <div className="flex mt-1.5">
                    {ENERGY_CLASSES.map((cls) => (
                        <div key={cls.label} className="flex-1 text-center">
                            <span className="text-[10px] text-muted-foreground">{cls.maxValue}</span>
                        </div>
                    ))}
                </div>

                {/* Active Class Label */}
                {classIdx >= 0 && (
                    <div
                        className="absolute -bottom-1 flex flex-col items-center"
                        style={{
                            left: `${((classIdx + 0.5) / ENERGY_CLASSES.length) * 100}%`,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div className="bg-foreground text-background text-xs font-bold px-2 py-0.5 rounded-sm">
                            Klasse {ENERGY_CLASSES[classIdx].label}
                        </div>
                    </div>
                )}
            </div>

            {/* Additional Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t text-sm">
                {yearBuilt && (
                    <div>
                        <p className="text-muted-foreground text-xs">Baujahr</p>
                        <p className="font-medium">{yearBuilt}</p>
                    </div>
                )}
                {condition && (
                    <div>
                        <p className="text-muted-foreground text-xs">Zustand</p>
                        <p className="font-medium">{condition}</p>
                    </div>
                )}
                {validUntil && (
                    <div>
                        <p className="text-muted-foreground text-xs">Gültig bis</p>
                        <p className="font-medium">{validUntil}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
